import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import vehicleMaintenanceApi from '../../api/vehicleMaintenanceApi';
import { VehicleModuleHero, VehiclePageShell } from './vehicleMaintenanceUi';
import DataTable from '../../components/ui/DataTable';
import FilterPanel from '../../components/ui/FilterPanel';
import FilterActions from '../../components/ui/FilterActions';
import { Input } from '../../components/ui/Input';

const ACTION_LABELS = {
  INSERT: 'Ekleme',
  UPDATE: 'Güncelleme',
  DELETE: 'Silme',
};

const InternalAuditVehicleAuditLogPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ search: '', action: '', table_name: '' });
  const [draftFilters, setDraftFilters] = useState({ search: '', action: '', table_name: '' });

  const loadData = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    try {
      const response = await vehicleMaintenanceApi.getAuditLogs({
        ...activeFilters,
        limit: 300,
      });
      setRows(response?.data || []);
    } catch (err) {
      const message = err.message || 'Denetim kayıtları yüklenemedi.';
      if (String(message).includes('404')) {
        setError(
          'Denetim log API bulunamadı (404). Backend’i yeniden başlatın ve migrate çalıştırın: python manage.py migrate internalaudit',
        );
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      {
        header: 'Zaman',
        accessorKey: 'created_at',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return '—';
          return new Date(value).toLocaleString('tr-TR');
        },
      },
      {
        header: 'Kullanıcı',
        accessorKey: 'user_name',
        cell: ({ getValue }) => getValue() || '—',
      },
      {
        header: 'Tablo',
        accessorKey: 'table_name',
      },
      {
        header: 'Kayıt',
        accessorKey: 'record_id',
      },
      {
        header: 'İşlem',
        accessorKey: 'action',
        cell: ({ getValue }) => ACTION_LABELS[getValue()] || getValue() || '—',
      },
      {
        header: 'Önceki',
        accessorKey: 'old_value',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return '—';
          return <span className="block max-w-[220px] truncate text-[10px]" title={value}>{value}</span>;
        },
      },
      {
        header: 'Sonraki',
        accessorKey: 'new_value',
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return '—';
          return <span className="block max-w-[220px] truncate text-[10px]" title={value}>{value}</span>;
        },
      },
    ],
    [],
  );

  return (
    <VehiclePageShell>
      <VehicleModuleHero
        icon={ClipboardDocumentListIcon}
        title="Denetim Logu"
        subtitle="Araç kayıtlarında kim neyi ne zaman değiştirdi."
        accent="slate"
      />

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <FilterPanel compact title="Filtreler" isOpen={isFilterOpen} onToggle={() => setIsFilterOpen((prev) => !prev)}>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <fieldset className="rounded border border-gray-200 bg-white px-2 py-1 dark:border-gray-600 dark:bg-gray-700">
            <legend className="px-0.5 text-[8px] text-gray-500">Arama</legend>
            <Input
              className="h-7 border-0 bg-transparent px-0 text-xs"
              value={draftFilters.search}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, search: e.target.value }))}
              placeholder="Kullanıcı, plaka..."
            />
          </fieldset>
          <fieldset className="rounded border border-gray-200 bg-white px-2 py-1 dark:border-gray-600 dark:bg-gray-700">
            <legend className="px-0.5 text-[8px] text-gray-500">İşlem</legend>
            <select
              className="h-7 w-full bg-transparent text-xs outline-none"
              value={draftFilters.action}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, action: e.target.value }))}
            >
              <option value="">Tümü</option>
              <option value="INSERT">Ekleme</option>
              <option value="UPDATE">Güncelleme</option>
              <option value="DELETE">Silme</option>
            </select>
          </fieldset>
          <fieldset className="rounded border border-gray-200 bg-white px-2 py-1 dark:border-gray-600 dark:bg-gray-700">
            <legend className="px-0.5 text-[8px] text-gray-500">Tablo</legend>
            <Input
              className="h-7 border-0 bg-transparent px-0 text-xs"
              value={draftFilters.table_name}
              onChange={(e) => setDraftFilters((prev) => ({ ...prev, table_name: e.target.value }))}
              placeholder="Vehicle"
            />
          </fieldset>
        </div>
        <div className="mt-1.5 flex justify-end border-t border-gray-100 pt-1.5 dark:border-gray-700">
          <FilterActions
            compact
            onClear={() => {
              const reset = { search: '', action: '', table_name: '' };
              setDraftFilters(reset);
              setFilters(reset);
              loadData(reset);
            }}
            onSearch={() => {
              setFilters(draftFilters);
              loadData(draftFilters);
            }}
          />
        </div>
      </FilterPanel>

      <DataTable data={rows} columns={columns} isLoading={loading} showSearch={false} compact modern />
    </VehiclePageShell>
  );
};

export default InternalAuditVehicleAuditLogPage;
